import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';

import { BlockEditor } from '@/components/editor/BlockEditor';
import { usePage } from '@/components/editor/use-page';
import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { PageHeader } from '@/components/layout/PageHeader';
import { useSidebar } from '@/components/layout/SidebarContext';
import { WorkspaceRootEmptyState } from '@/components/layout/WorkspaceRootEmptyState.web';
import { createBlock, fetchBlockTree, fetchCurrentMember } from '@/lib/blocks/api';
import { newBlockId } from '@/lib/blocks/ids';
import { updateCachedPageTitle } from '@/lib/blocks/page-cache';
import type { BlockRecord } from '@/lib/blocks/types';
import { workspaceRootKind } from '@/lib/blocks/workspace-empty';
import { setLastPageId } from '@/lib/navigation/last-page';
import { pushRecentPage, removeRecentPage } from '@/lib/navigation/recent-pages';

type PageViewProps = {
  pageId: string | undefined;
  trackRecents?: boolean;
};

export function PageView({ pageId, trackRecents = true }: PageViewProps) {
  const router = useRouter();
  const { ensureChildren, addChild, updateChild } = useSidebar();
  const {
    loading,
    error,
    block,
    contentBlocks,
    initialContent,
    syncVersions,
    contentRevision,
    reloadFromRemote,
    applyRemotePageBlock,
  } = usePage(pageId);
  const serverPositions = useMemo(
    () => new Map(contentBlocks.map((row) => [row.id, row.position])),
    [contentBlocks],
  );
  const [pageTitle, setPageTitle] = useState('');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [childPageCount, setChildPageCount] = useState<number | null>(null);

  useEffect(() => {
    void fetchCurrentMember()
      .then((member) => setMemberId(member.id))
      .catch(() => setMemberId(null));
  }, []);

  useEffect(() => {
    if (!trackRecents || !block) {
      return;
    }
    setPageTitle(block.title ?? 'Untitled');
    void setLastPageId(block.id);
    pushRecentPage({
      id: block.id,
      title: block.title ?? 'Untitled',
      type: block.type === 'database' ? 'database' : 'page',
    });
  }, [block?.id, block?.title, block?.type, trackRecents]);

  useEffect(() => {
    if (!trackRecents || !pageId || loading || block) {
      return;
    }
    if (error) {
      removeRecentPage(pageId);
    }
  }, [pageId, loading, block, error, trackRecents]);

  useEffect(() => {
    if (!block || !memberId) {
      setChildPageCount(null);
      return;
    }
    const rootKind = workspaceRootKind(block, memberId);
    if (!rootKind) {
      setChildPageCount(null);
      return;
    }
    let cancelled = false;
    void ensureChildren(block.id).then((children) => {
      if (!cancelled) {
        setChildPageCount(children.length);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [block, memberId, ensureChildren]);

  async function onCreateWorkspacePage(): Promise<void> {
    if (!block) {
      return;
    }
    const siblings = await fetchBlockTree(block.id);
    const pageSiblings = siblings.filter((row) => row.type === 'page' || row.type === 'database');
    const created = await createBlock({
      id: newBlockId(),
      parentId: block.id,
      type: 'page',
      title: 'Untitled',
      position: pageSiblings.length,
    });
    addChild(block.id, created);
    setChildPageCount(pageSiblings.length + 1);
    router.push(`/page/${created.id}`);
  }

  if (!pageId) {
    return (
      <View className="flex-1 items-center justify-center px-6">
        <Text className="text-base text-fg-muted">Missing page id</Text>
      </View>
    );
  }

  if (loading && !block) {
    return (
      <View className="min-h-full flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error || !block) {
    return (
      <View className="flex-1 items-center justify-center px-6">
        <Text className="text-base text-fg-muted">{error ?? 'Page not found'}</Text>
      </View>
    );
  }

  const displayTitle = pageTitle || block.title || 'Untitled';
  const rootKind = memberId ? workspaceRootKind(block, memberId) : null;
  const showWorkspaceEmpty = rootKind != null && childPageCount === 0;
  const currentBlock = block;

  function handlePageTitleChange(title: string): void {
    setPageTitle(title);
    updateCachedPageTitle(currentBlock.id, title);
    if (currentBlock.parentId) {
      updateChild(currentBlock.parentId, currentBlock.id, { title });
    }
  }

  function handleRemotePageBlock(row: BlockRecord): void {
    if (row.syncVersion <= currentBlock.syncVersion) {
      return;
    }
    applyRemotePageBlock(row);
    const title = row.title ?? 'Untitled';
    setPageTitle(title);
    updateCachedPageTitle(row.id, title);
  }

  return (
    <>
      <DocumentTitle title={displayTitle} />
      <View className="min-h-0 flex-1 bg-surface">
        <PageHeader
          block={block}
          displayTitle={displayTitle}
          onTitleChange={(title) => {
            setPageTitle(title);
            updateCachedPageTitle(block.id, title);
          }}
        />
        {showWorkspaceEmpty ? (
          <WorkspaceRootEmptyState
            kind={rootKind}
            onCreatePage={() => void onCreateWorkspacePage()}
          />
        ) : (
          <BlockEditor
            key={`${pageId}-${contentRevision}`}
            initialContent={initialContent}
            loading={loading}
            onPageTitleChange={handlePageTitleChange}
            onRemotePageBlock={handleRemotePageBlock}
            onRemoteReload={reloadFromRemote}
            pageId={pageId}
            pageSyncVersion={currentBlock.syncVersion}
            pageTitle={displayTitle}
            serverPositions={serverPositions}
            syncVersions={syncVersions}
          />
        )}
      </View>
    </>
  );
}
